import { Button } from '@repo/ui/button'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { genWidths } from '@repo/ui/utils'
import { Minus, Plus } from 'lucide-react'
import { memo } from 'react'

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardFooter,
  CollapsibleCardHeader,
  CollapsibleCardLeftButtons,
  CollapsibleCardTitle,
} from '@/components/CollapsibleCard'
import { TaskCategories, TaskDueDate, TaskLink } from '@/components/tasks/Task'
import { useTrace, useView } from '@/hooks'
import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { Task } from '@/store/task'

type Props = {
  isFound: boolean
  task: Task
  savedTasks: Task[]
}

export const AssistantTask = memo(function ({ task }: { task: Task }) {
  const dispatch = useStoreDispatch()
  const { Trace } = useTrace({ traceUrl: task.traceUrl })

  const {
    data: [tasks],
  } = useAsyncData([(state) => state.task.current])

  const savedTasks = tasks || []
  const isFound = savedTasks.some(({ title }) => title === task.title)
  return (
    <CollapsibleCard variant="secondary">
      <CollapsibleCardHeader>
        <CollapsibleCardLeftButtons>
          <SaveTaskButton savedTasks={savedTasks} isFound={isFound} task={task} />
        </CollapsibleCardLeftButtons>

        <CollapsibleCardTitle className={isFound ? 'data-[state=closed]:text-primary-500' : ''}>
          {task.title}
        </CollapsibleCardTitle>
      </CollapsibleCardHeader>

      <CollapsibleCardContent>
        <p className="whitespace-pre-wrap text-pretty">{task.content}</p>

        <>
          <TaskLink className="text-primary-foreground bg-transparent" task={task} />
          <TaskCategories task={task} />
        </>

        <CollapsibleCardFooter className="justify-end">
          <Trace />
          <TaskDueDate
            initialDueDate={task.dueDate}
            onDueDateChange={(dueDate) => {
              void dispatch(
                storeActions.task.updateDueDate({
                  taskId: task.id,
                  dueDate: dueDate?.getTime(),
                }),
              )
            }}
          />
        </CollapsibleCardFooter>
      </CollapsibleCardContent>
    </CollapsibleCard>
  )
})

export function TaskLoader({ className }: { className?: string }) {
  return (
    <CollapsibleCard variant="secondary" className={className}>
      <CollapsibleCardHeader className="h-10 select-none bg-opacity-30">
        <Button
          variant="link"
          size="icon"
          className="text-primary-200 hover:text-primary-foreground h-full transition-colors"
          disabled
        >
          <Plus className="size-5" />
        </Button>

        <div className="flex h-full flex-1 items-center px-3">
          <div
            className="bg-primary-300 h-4 animate-pulse rounded"
            style={{ width: genWidths(1)[0] + '%' }}
          />
        </div>
      </CollapsibleCardHeader>
    </CollapsibleCard>
  )
}

function SaveTaskButton({ savedTasks, isFound, task }: Props) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const view = useView()

  const handleDelete = () => {
    const taskToDelete = savedTasks.find(
      (t) => t.title === task.title && t.content === task.content,
    )
    if (!taskToDelete) return

    view.beneficiary().tasks.navigate()

    void dispatch(
      storeActions.task.delete({
        task: taskToDelete,
        notify: false,
      }),
    )
  }

  const handleSave = () => {
    if (isFound) return

    view.beneficiary().tasks.navigate()

    void dispatch(
      storeActions.task.create({
        fields: {
          categories: task.categories,
          content: task.content,
          cta: task.cta,
          dueDate: task.dueDate,
          durationInHours: task.durationInHours,
          title: task.title,
        },
      }),
    )
  }
  return (
    <>
      {isFound ? (
        <TooltipWrapper content={tc('removeTheTask', { cfl: true })}>
          <Button
            variant="link"
            size="icon"
            className="text-primary-200 hover:text-primary-foreground min-h-full transition-colors"
            onClick={handleDelete}
          >
            <Minus className="size-5" />
          </Button>
        </TooltipWrapper>
      ) : (
        <TooltipWrapper content={tc('addTheTask')}>
          <Button
            variant="link"
            size="icon"
            className="text-primary-200 hover:text-primary-foreground min-h-full transition-colors"
            onClick={handleSave}
          >
            <Plus className="size-5" />
          </Button>
        </TooltipWrapper>
      )}
    </>
  )
}
